// components/VideoPlayer.jsx - Handle video playback with proper error handling
import React, { useState, useRef, useEffect, useCallback, memo } from "react";
import {
  Play,
  Pause,
  Download,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  AlertTriangle,
  MessageSquarePlus,
  ArrowUp,
  MessageCircle,
  // `Share`, not `Share2`. Share2 is the three-connected-nodes Android glyph; the box-with-an
  // -arrow-leaving-it is what macOS, iOS and Safari all use, which is what people on this app
  // recognise as "share" without reading a tooltip.
  Share,
} from "lucide-react";
import { GENERIC_ERROR_MESSAGE } from "../utils/constants";
import { ROW_TITLE_FONT, CLAMP_2_LINES, safeRenderLatex } from "../utils/videoTitle";
import VideoQuestionPanel from "./VideoQuestionPanel";
import { useT } from "../i18n/useT";

const VideoPlayer = ({
  videoUrl,
  subtitleUrl,
  title = "Generated Video",
  // Draw `title` across the top of the frame. Opt-in: `title` is passed on almost every
  // surface (it also feeds the download filename), so making the overlay automatic would
  // stamp a caption onto thumbnails and inline previews that never asked for one.
  showTitleOverlay = false,
  // Video id for the pause-to-ask panel. Presence of an id is what enables the panel, so the
  // caller gates it (on sign-in, on the transcript existing) by simply not passing one — the
  // player stays ignorant of who is allowed to ask questions.
  chatVideoId = null,
  // Set when the viewer is signed out: the Ask button calls this instead of opening the panel.
  // The button stays visible either way — /api/video-chat refuses an anonymous caller, and an
  // invitation to sign in is a better answer than a control that quietly is not there.
  onRequireAccount = null,
  onDownload,
  isDownloading = false,
  downloadProgress = 0,
  className = "",
  onEnded,
  onWatchProgress,
  onPlay,
  autoPlay = true,
  hideControls = false,
  onFollowUp,
  // Optional chat affordance, rendered beside CC. A prop rather than something the player
  // owns: only session pages have a tutor to talk to, and the player must stay usable on
  // the pages that do not.
  onChat,
  // Optional share affordance, beside CC for the same reason onChat is: it acts on the
  // thing you are WATCHING. Only pages that own a share sheet pass it, so the player stays
  // usable on the ones that do not.
  onShare,
  // WHEN THE PLAYER'S CHROME IS UP, reported outward. The title, the control bar and the Ask
  // pill already share one visibility contract — shown on pointer enter/move, hidden after
  // 3s of stillness, gone on leave — and a page that wants to put a title BESIDE the player
  // rather than on it has no way to obey the same rule. CSS :hover cannot: it has no notion
  // of "the mouse stopped moving", so a page-level title would sit there long after the
  // controls under it had faded. This hands over the state itself.
  onChromeChange = null,
  // A CSS length of black to add above AND below the picture, turning the player into a
  // letterboxed frame. Null means the player is exactly its picture, which is the default
  // everywhere else.
  frameMat = null,
  // Lets a page render the Ask pill somewhere the player cannot reach — the black bar above
  // the frame, say. The PANEL still belongs to the player; only the button moves, and
  // `askOpen`/`onAskOpenChange` make it controlled so both live on one piece of state.
  hideAskButton = false,
  askOpen: askOpenProp,
  onAskOpenChange = null,
}) => {
  const t = useT();
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);
  const subtitleRetryRef = useRef(0);
  const subtitleRetryTimerRef = useRef(null);
  const [subtitleKey, setSubtitleKey] = useState(0); // forces <track> remount on retry
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoPlayPending, setAutoPlayPending] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(false);
  // True only when WE muted the video to get autoplay past the browser policy — drives the
  // "Tap for sound" prompt. Distinct from isMuted, which the viewer controls deliberately.
  const [autoMuted, setAutoMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSeekable, setIsSeekable] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [subtitlesEnabled, setSubtitlesEnabled] = useState(false); // Off by default, user toggles on
  const [hasSubtitles, setHasSubtitles] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isHoldingForSpeed, setIsHoldingForSpeed] = useState(false);
  const [speedBeforeHold, setSpeedBeforeHold] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showControls, setShowControls] = useState(true);
  // Whether the question panel is actually OPEN. It used to be derived straight from "paused",
  // which meant a full panel threw itself across a third of the frame every time anyone
  // stopped the video — including people who had no idea the feature existed and were only
  // trying to look at the frame underneath. Pausing now offers a labelled button and this
  // flag is what the button sets, so the panel is only ever there because someone asked.
  const [askOpenLocal, setAskOpenLocal] = useState(false);
  /* CONTROLLED WHEN A PAGE ASKS TO BE, uncontrolled otherwise — so every existing caller
     behaves exactly as it did and the one page that hosts its own button drives the same
     panel rather than growing a second one. */
  const askOpen = askOpenProp !== undefined ? askOpenProp : askOpenLocal;
  const setAskOpen = onAskOpenChange || setAskOpenLocal;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [bufferedEnd, setBufferedEnd] = useState(0); // End of buffered range in seconds
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    x: 0,
    y: 0,
  });
  const [currentCue, setCurrentCue] = useState(""); // For custom subtitle rendering
  const [captionSize, setCaptionSize] = useState(() => {
    const saved = localStorage.getItem("captionSize");
    return saved ? parseInt(saved, 10) : 18;
  });
  const [previewTime, setPreviewTime] = useState(null); // null = hidden
  const [previewX, setPreviewX] = useState(0); // horizontal position in px relative to progress bar
  const [previewFrame, setPreviewFrame] = useState(0); // increments on each drawn frame to trigger re-render
  const previewVideoRef = useRef(null);
  const previewCanvasRef = useRef(null); // offscreen canvas for drawing
  const progressBarRef = useRef(null);
  const previewSeekRAF = useRef(null);
  const wasHoldingRef = useRef(false);
  const speedMenuRef = useRef(null);
  const followUpRef = useRef(null);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followUpText, setFollowUpText] = useState("");
  const holdTimeoutRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const clickTimeoutRef = useRef(null);
  // Watch progress tracking refs
  const maxPositionRef = useRef(0);
  const watchedTimeRef = useRef(0);
  const lastHeartbeatRef = useRef(Date.now());
  // Auto-hide controls after 3 seconds of no mouse movement
  const resetControlsTimeout = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (!showSpeedMenu) {
        setShowControls(false);
      }
    }, 3000);
  };

  /* IS THE POINTER ACTUALLY OVER THE PLAYER. `showControls` alone cannot answer that: it
     starts TRUE so the chrome is visible on arrival, and nothing lowers it until the first
     pointer event — so a page riding it would show its title from load, which is the opposite
     of "only while hovering". Reported as the AND of the two. */
  const [pointerInside, setPointerInside] = useState(false);

  // The same contract the control bar, the in-frame title and the Ask pill ride — handed to
  // the page so anything placed beside the player obeys it rather than inventing a second
  // rule out of :hover, which has no notion of "the mouse stopped moving".
  useEffect(() => {
    if (onChromeChange) onChromeChange(showControls && pointerInside);
  }, [showControls, pointerInside, onChromeChange]);

  const handleMouseMove = () => {
    setPointerInside(true);
    resetControlsTimeout();
  };

  const handleMouseLeave = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    setPointerInside(false);
    setShowControls(false);
  };

  const handleMouseEnter = () => {
    setPointerInside(true);
    resetControlsTimeout();
  };

  // Close speed menu when clicking outside (with overlay to prevent propagation)
  useEffect(() => {
    if (showSpeedMenu) {
      const handleClickOutside = (e) => {
        if (speedMenuRef.current && !speedMenuRef.current.contains(e.target)) {
          e.stopPropagation();
          e.preventDefault();
          setShowSpeedMenu(false);
        }
      };
      // Use capture phase to catch the event before it reaches other elements
      document.addEventListener("click", handleClickOutside, true);
      return () =>
        document.removeEventListener("click", handleClickOutside, true);
    }
  }, [showSpeedMenu]);

  // Close follow-up panel when clicking outside
  useEffect(() => {
    if (showFollowUp) {
      const handleClickOutside = (e) => {
        if (followUpRef.current && !followUpRef.current.contains(e.target)) {
          e.stopPropagation();
          e.preventDefault();
          setShowFollowUp(false);
        }
      };
      document.addEventListener("click", handleClickOutside, true);
      return () =>
        document.removeEventListener("click", handleClickOutside, true);
    }
  }, [showFollowUp]);

  const handleFollowUpSend = () => {
    if (!followUpText.trim() || !onFollowUp) return;
    onFollowUp(followUpText.trim());
    setFollowUpText("");
    setShowFollowUp(false);
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch((err) => {
          console.error("Error playing video:", err);
          setError(GENERIC_ERROR_MESSAGE);
        });
      }
    }
  };

  const handleMuteUnmute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
      setAutoMuted(false); // the viewer has taken control; stop nudging them
    }
  };

  const handleSpeedChange = (speed) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
      setPlaybackSpeed(speed);
      setShowSpeedMenu(false);
    }
  };

  const handleSpeedHoldStart = () => {
    // Only start hold-to-2x after 150ms delay so quick clicks still work for play/pause
    if (videoRef.current && isPlaying) {
      holdTimeoutRef.current = setTimeout(() => {
        if (videoRef.current) {
          setSpeedBeforeHold(playbackSpeed);
          setIsHoldingForSpeed(true);
          wasHoldingRef.current = true;
          videoRef.current.playbackRate = 2;
        }
      }, 150);
    }
  };

  const handleSpeedHoldEnd = () => {
    // Clear the timeout if released before 150ms (it was a click, not a hold)
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    if (isHoldingForSpeed && videoRef.current) {
      videoRef.current.playbackRate = speedBeforeHold;
      setPlaybackSpeed(speedBeforeHold);
      setIsHoldingForSpeed(false);
    }
  };

  const handleVideoClick = () => {
    // Skip play/pause if we just finished a speed hold
    if (wasHoldingRef.current) {
      wasHoldingRef.current = false;
      return;
    }
    // Delay play/pause to distinguish from double-click
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    clickTimeoutRef.current = setTimeout(() => {
      handlePlayPause();
    }, 300);
  };

  const handleVideoDoubleClick = () => {
    // Cancel the pending single-click action
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    handleFullscreen();
  };

  const contextMenuRef = useRef(null);

  const handleContextMenu = (e) => {
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    setContextMenuPosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setShowContextMenu(true);
  };

  // Close context menu on click-away, scroll, or Escape
  useEffect(() => {
    if (!showContextMenu) return;
    const close = () => setShowContextMenu(false);
    const handleKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("click", close);
    document.addEventListener("scroll", close, true);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("scroll", close, true);
      document.removeEventListener("keydown", handleKey);
    };
  }, [showContextMenu]);

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      // Track buffered range end for progress bar
      if (videoRef.current.buffered.length > 0) {
        setBufferedEnd(
          videoRef.current.buffered.end(videoRef.current.buffered.length - 1),
        );
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      setIsLoading(false);
      setError(null);

      // Re-apply playback speed (browser resets playbackRate when loading a new source)
      if (playbackSpeed !== 1) {
        videoRef.current.playbackRate = playbackSpeed;
      }

      // Check if video is seekable
      if (videoRef.current.seekable.length > 0) {
        setIsSeekable(true);
      }

      // Detect text tracks (embedded soft subtitles or external <track>)
      try {
        const tracks = videoRef.current.textTracks;
        const trackCount = tracks ? tracks.length : 0;
        setHasSubtitles(trackCount > 0);

        // If subtitles are present, always hide native rendering (we use custom overlay)
        if (trackCount > 0) {
          for (let i = 0; i < tracks.length; i++) {
            tracks[i].mode = "hidden"; // Always hidden - we render subtitles ourselves
            // Attempt to set language/label defaults if browser supports
            if (!tracks[i].label) {
              try {
                tracks[i].label = "English";
              } catch {}
            }
          }
        }
      } catch (e) {
        console.warn("Subtitle track detection failed:", e);
        setHasSubtitles(false);
      }
    }
  };

  const handleVideoError = (e) => {
    const errorCode = e.target.error?.code;
    console.error("Video error:", e);
    console.error("Video error details:", {
      videoUrl,
      error: e.target.error,
      errorCode,
      errorMessage: e.target.error?.message,
      networkState: e.target.networkState,
      readyState: e.target.readyState,
      currentSrc: e.target.currentSrc,
      retryCount: retryCountRef.current,
    });

    // Auto-retry for transient errors (e.g. video not in DB yet after generation)
    // Error code 4 (SRC_NOT_SUPPORTED) often means a 404 during the Firestore save window
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 2500;
    if (
      retryCountRef.current < MAX_RETRIES &&
      (errorCode === 4 || errorCode === 2)
    ) {
      retryCountRef.current += 1;
      console.log(
        `🔄 Retrying video load (${retryCountRef.current}/${MAX_RETRIES}) in ${RETRY_DELAY_MS}ms...`,
      );
      setError(null);
      setIsLoading(true);
      retryTimerRef.current = setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.load();
        }
      }, RETRY_DELAY_MS);
      return;
    }

    // Max retries exhausted — show permanent error
    setError(GENERIC_ERROR_MESSAGE);
    setIsLoading(false);
  };

  const toggleSubtitles = () => {
    if (!hasSubtitles) return;
    // Just toggle the state - our custom overlay handles rendering
    setSubtitlesEnabled((prev) => !prev);
  };

  // When an external subtitle URL is provided or subtitle toggle changes, update track mode
  useEffect(() => {
    if (!videoRef.current) return;

    const updateTrackMode = () => {
      try {
        const tracks = videoRef.current.textTracks;
        if (!tracks) return;

        console.log("🎬 Updating subtitle track mode:", {
          trackCount: tracks.length,
          subtitlesEnabled: subtitlesEnabled,
        });

        if (tracks.length > 0) {
          setHasSubtitles(true);
          // Always keep native tracks hidden - we render subtitles ourselves
          for (let i = 0; i < tracks.length; i++) {
            tracks[i].mode = "hidden";
            console.log(
              `🎬 Track ${i}: mode=${tracks[i].mode}, readyState=${tracks[i].readyState}`,
            );
          }
        }
      } catch (e) {
        console.warn("Track update error:", e);
      }
    };

    // Update immediately when subtitle state or URL changes
    updateTrackMode();

    // Also listen for track addition
    if (videoRef.current.textTracks) {
      const handleAddTrack = () => {
        console.log("🎬 New track added, updating mode");
        setTimeout(updateTrackMode, 100); // Small delay to ensure track is ready
      };

      videoRef.current.textTracks.addEventListener("addtrack", handleAddTrack);
      return () => {
        if (videoRef.current?.textTracks) {
          videoRef.current.textTracks.removeEventListener(
            "addtrack",
            handleAddTrack,
          );
        }
      };
    }
  }, [subtitleUrl, subtitlesEnabled]); // Include subtitlesEnabled to respond to toggle

  // Listen for cue changes to render custom subtitles
  useEffect(() => {
    if (!videoRef.current) return;
    const tracks = videoRef.current.textTracks;
    if (!tracks || tracks.length === 0) return;

    const track = tracks[0];

    const handleCueChange = () => {
      if (track.activeCues && track.activeCues.length > 0) {
        // Get all active cues and join them (in case of multiple lines)
        const cueTexts = Array.from(track.activeCues).map((cue) => cue.text);
        setCurrentCue(cueTexts.join("\n"));
      } else {
        setCurrentCue("");
      }
    };

    // Set track to hidden - we'll render subtitles ourselves
    track.mode = "hidden";

    track.addEventListener("cuechange", handleCueChange);
    return () => {
      track.removeEventListener("cuechange", handleCueChange);
    };
  }, [subtitleUrl, hasSubtitles, subtitlesEnabled]);

  // Effective duration
  const effectiveDuration = duration && isFinite(duration) ? duration : 0;

  const handleSeekTouch = (e) => {
    if (e.changedTouches && e.changedTouches.length > 0) {
      const touch = e.changedTouches[0];
      e.preventDefault();
      handleSeek({ currentTarget: e.currentTarget, clientX: touch.clientX });
    } else {
      handleSeek(e);
    }
  };

  const handleSeek = (e) => {
    if (!videoRef.current || !videoReady) {
      return;
    }

    if (!isSeekable) {
      return;
    }

    const seekDuration = effectiveDuration || duration;
    if (!seekDuration || seekDuration === 0) {
      return;
    }

    try {
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      const newTime = pos * seekDuration;

      // Ensure we're seeking within seekable/buffered range
      if (videoRef.current.seekable.length > 0) {
        const seekStart = videoRef.current.seekable.start(0);
        const seekEnd = videoRef.current.seekable.end(0);
        const clampedTime = Math.max(seekStart, Math.min(seekEnd, newTime));

        videoRef.current.currentTime = clampedTime;
        setCurrentTime(clampedTime);
      }
    } catch (error) {
      console.error("Error seeking video:", error);
    }
  };

  // --- Preview thumbnail on progress bar hover ---
  const handleProgressHover = useCallback(
    (e) => {
      if (!effectiveDuration || !progressBarRef.current) return;
      const rect = progressBarRef.current.getBoundingClientRect();
      const pos = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const time = pos * effectiveDuration;
      setPreviewTime(time);
      setPreviewX(e.clientX - rect.left);

      // Seek the hidden preview video via rAF to avoid thrashing
      if (previewVideoRef.current && !previewSeekRAF.current) {
        previewSeekRAF.current = requestAnimationFrame(() => {
          previewSeekRAF.current = null;
          if (previewVideoRef.current) {
            previewVideoRef.current.currentTime = time;
          }
        });
      }
    },
    [effectiveDuration],
  );

  const handleProgressLeave = useCallback(() => {
    setPreviewTime(null);
    if (previewSeekRAF.current) {
      cancelAnimationFrame(previewSeekRAF.current);
      previewSeekRAF.current = null;
    }
  }, []);

  // Create offscreen canvas once; draw frame when the preview video seeks
  useEffect(() => {
    if (!previewCanvasRef.current) {
      previewCanvasRef.current = document.createElement("canvas");
    }
    const pv = previewVideoRef.current;
    if (!pv) return;
    const draw = () => {
      const canvas = previewCanvasRef.current;
      if (!canvas || !pv.videoWidth) return;
      const w = 160;
      const h = Math.round(w * (pv.videoHeight / pv.videoWidth));
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(pv, 0, 0, w, h);
      setPreviewFrame((n) => n + 1); // trigger re-render so tooltip picks up new frame
    };
    pv.addEventListener("seeked", draw);
    return () => pv.removeEventListener("seeked", draw);
  }, [videoUrl]);

  const formatTime = (time) => {
    if (isNaN(time) || !isFinite(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleFullscreen = () => {
    // iOS Safari: Fullscreen API does not work on <div> containers.
    // Fall back to webkitEnterFullscreen() on the <video> element itself.
    const isIOSSafari =
      !document.fullscreenEnabled &&
      !document.webkitFullscreenEnabled &&
      videoRef.current?.webkitEnterFullscreen;

    if (isIOSSafari) {
      try {
        videoRef.current.webkitEnterFullscreen();
      } catch (err) {
        console.error("iOS fullscreen error:", err);
      }
      return;
    }

    if (containerRef.current) {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
      } else {
        if (containerRef.current.requestFullscreen) {
          containerRef.current.requestFullscreen();
        } else if (containerRef.current.webkitRequestFullscreen) {
          containerRef.current.webkitRequestFullscreen();
        } else if (containerRef.current.msRequestFullscreen) {
          containerRef.current.msRequestFullscreen();
        }
      }
    }
  };

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!videoRef.current || !containerRef.current) return;

      // Only handle keys when the video player is focused or hovered
      if (
        !containerRef.current.contains(document.activeElement) &&
        !containerRef.current.matches(":hover")
      )
        return;

      switch (e.key) {
        case " ":
          e.preventDefault();
          handlePlayPause();
          break;
        case "ArrowLeft":
          e.preventDefault();
          if (isSeekable) {
            try {
              const newTime = Math.max(0, videoRef.current.currentTime - 5);
              videoRef.current.currentTime = newTime;
            } catch (error) {
              console.error("Error seeking with keyboard:", error);
            }
          }
          break;
        case "ArrowRight":
          e.preventDefault();
          if (isSeekable) {
            try {
              const newTime = Math.min(
                duration,
                videoRef.current.currentTime + 5,
              );
              videoRef.current.currentTime = newTime;
            } catch (error) {
              console.error("Error seeking with keyboard:", error);
            }
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          if (isSeekable) {
            try {
              videoRef.current.currentTime = Math.min(
                duration,
                videoRef.current.currentTime + 10,
              );
            } catch (error) {
              console.error("Error seeking with keyboard:", error);
            }
          }
          break;
        case "ArrowDown":
          e.preventDefault();
          if (isSeekable) {
            try {
              videoRef.current.currentTime = Math.max(
                0,
                videoRef.current.currentTime - 10,
              );
            } catch (error) {
              console.error("Error seeking with keyboard:", error);
            }
          }
          break;
        case "m":
          e.preventDefault();
          handleMuteUnmute();
          break;
        case "f":
          e.preventDefault();
          handleFullscreen();
          break;
        case "c":
        case "C":
          e.preventDefault();
          toggleSubtitles();
          break;
        case "-":
          e.preventDefault();
          setCaptionSize((prev) => {
            const next = Math.max(12, prev - 2);
            localStorage.setItem("captionSize", next);
            return next;
          });
          break;
        case "=":
          e.preventDefault();
          setCaptionSize((prev) => {
            const next = Math.min(36, prev + 2);
            localStorage.setItem("captionSize", next);
            return next;
          });
          break;
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [duration, isPlaying, isSeekable]);

  // Track fullscreen state changes (including iOS native video fullscreen)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        !!(document.fullscreenElement || document.webkitFullscreenElement),
      );
    };
    const handleiOSFullscreenBegin = () => setIsFullscreen(true);
    const handleiOSFullscreenEnd = () => setIsFullscreen(false);

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    const video = videoRef.current;
    if (video) {
      video.addEventListener("webkitbeginfullscreen", handleiOSFullscreenBegin);
      video.addEventListener("webkitendfullscreen", handleiOSFullscreenEnd);
    }

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      if (video) {
        video.removeEventListener(
          "webkitbeginfullscreen",
          handleiOSFullscreenBegin,
        );
        video.removeEventListener(
          "webkitendfullscreen",
          handleiOSFullscreenEnd,
        );
      }
    };
  }, []);

  // Auto-focus container so keyboard shortcuts work immediately
  useEffect(() => {
    if (videoReady && containerRef.current) {
      containerRef.current.focus();
    }
  }, [videoReady]);

  // Watch progress heartbeat — fires every 30s while playing
  useEffect(() => {
    if (!onWatchProgress || !isPlaying) return;

    const sendHeartbeat = () => {
      if (!videoRef.current) return;
      const now = Date.now();
      const elapsed = Math.min((now - lastHeartbeatRef.current) / 1000, 35); // cap at 35s for backgrounded tabs
      lastHeartbeatRef.current = now;
      watchedTimeRef.current += elapsed;
      maxPositionRef.current = Math.max(
        maxPositionRef.current,
        videoRef.current.currentTime,
      );
      const videoDur = videoRef.current.duration || 0;
      onWatchProgress({
        watchedSeconds: watchedTimeRef.current,
        videoDuration: videoDur,
        maxPosition: maxPositionRef.current,
        completed: videoDur > 0 && maxPositionRef.current / videoDur >= 0.9,
      });
    };

    lastHeartbeatRef.current = Date.now();
    const interval = setInterval(sendHeartbeat, 5000);
    return () => clearInterval(interval);
  }, [isPlaying, onWatchProgress]);

  // Auto-play when prop is set and video is ready (after splash is gone)
  useEffect(() => {
    if (!autoPlay || !videoReady || !videoRef.current || isPlaying) return;

    const tryPlay = () => {
      const el = videoRef.current;
      if (!el) return;
      el.play().catch(() => {
        // Browsers block UNMUTED autoplay without a prior user gesture, which is why
        // arriving from an email/share link left the video sitting paused. Muted autoplay
        // is always permitted, so fall back to it and surface an unmute prompt — these are
        // narrated lessons, so silent playback must never be allowed to go unnoticed.
        el.muted = true;
        setIsMuted(true);
        setAutoMuted(true);
        el.play()
          .then(() => setAutoPlayPending(false))
          .catch(() => {
            setAutoMuted(false);
            setAutoPlayPending(false);
          });
      });
    };

    // If splash screen is still visible, wait for it to be removed
    const splash = document.getElementById('splash');
    if (splash) {
      setAutoPlayPending(true);
      const observer = new MutationObserver(() => {
        if (!document.getElementById('splash')) {
          observer.disconnect();
          tryPlay();
        }
      });
      observer.observe(document.body, { childList: true });
      return () => observer.disconnect();
    }

    tryPlay();
  }, [autoPlay, videoReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset media state when videoUrl changes so the previous frame cannot flash.
  useEffect(() => {
    retryCountRef.current = 0;
    maxPositionRef.current = 0;
    watchedTimeRef.current = 0;
    lastHeartbeatRef.current = Date.now();
    setError(null);
    setIsLoading(Boolean(videoUrl));
    setVideoReady(false);
    setIsSeekable(false);
    setBufferedEnd(0);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [videoUrl]);

  // Reset subtitle retry when subtitleUrl changes; clean up on unmount
  useEffect(() => {
    subtitleRetryRef.current = 0;
    setSubtitleKey(0);
    return () => {
      if (subtitleRetryTimerRef.current)
        clearTimeout(subtitleRetryTimerRef.current);
    };
  }, [subtitleUrl]);

  // Debug video and subtitle URLs
  useEffect(() => {
    console.log("VideoPlayer initialized with:", {
      videoUrl,
      subtitleUrl,
      isFirebaseUrl: videoUrl?.includes("firebasestorage"),
      isApiUrl: videoUrl?.includes("/api/video/"),
      urlLength: videoUrl?.length,
      hasSubtitleUrl: !!subtitleUrl,
    });
  }, [videoUrl, subtitleUrl]);

  return (
    <div
      ref={containerRef}
      className={`bg-black overflow-hidden relative ${className} ${isFullscreen ? "" : "rounded-lg"}`}
      /* LETTERBOX, the way a player does it — not a black div stacked on top of one.
         The bars are this container's own background showing past a picture that no longer
         fills it: same black, inside the same `rounded-lg overflow-hidden`, no seam where
         bar meets frame.

         It has to be padding HERE rather than on the aspect-video holder below, because the
         control bar, the title and the subtitles are positioned against THIS box. Padding
         pushes the box's bottom edge down past the picture and the controls ride down with
         it, so they hover over the bottom bar instead of sitting on the video's last row.
         That is the point of the prop — shrink the picture any other way and the controls
         are stranded above the black.

         Off in fullscreen: there the picture is already letterboxed by the screen. */
      style={frameMat && !isFullscreen
        ? { paddingTop: frameMat, paddingBottom: frameMat }
        : undefined}
      tabIndex={0}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      onTouchStart={handleMouseMove}
    >
      {/* Video Element */}
      <div
        className={`relative ${isFullscreen ? "h-full w-full flex items-center justify-center" : "aspect-video"} ${!showControls ? "cursor-none" : ""}`}
      >
        <video
          key={videoUrl || "empty-video"}
          ref={videoRef}
          src={videoUrl}
          className="w-full h-full object-contain"
          style={{
            "--webkit-media-text-track-display": "block",
            opacity: isLoading ? 0 : 1,
            transition: isLoading
              ? "opacity 120ms ease"
              : "opacity 300ms ease 120ms",
          }}
          data-subtitle-offset="true"
          onPlay={() => {
            setIsPlaying(true);
            setAutoPlayPending(false);
            onPlay?.();
          }}
          onPause={() => {
            setIsPlaying(false);
            // Pausing DISMISSES the panel rather than opening it. Stopping the video is how
            // you get a clear look at the frame, and a panel covering a third of it is the
            // opposite of what that gesture asks for. The hover pill is how it comes back.
            setAskOpen(false);
            // Final heartbeat on pause
            if (onWatchProgress && videoRef.current) {
              const now = Date.now();
              const elapsed = Math.min(
                (now - lastHeartbeatRef.current) / 1000,
                35,
              );
              lastHeartbeatRef.current = now;
              watchedTimeRef.current += elapsed;
              maxPositionRef.current = Math.max(
                maxPositionRef.current,
                videoRef.current.currentTime,
              );
              const videoDur = videoRef.current.duration || 0;
              onWatchProgress({
                watchedSeconds: watchedTimeRef.current,
                videoDuration: videoDur,
                maxPosition: maxPositionRef.current,
                completed:
                  videoDur > 0 && maxPositionRef.current / videoDur >= 0.9,
              });
            }
          }}
          onEnded={() => {
            setIsPlaying(false);
            // Final heartbeat on end
            if (onWatchProgress && videoRef.current) {
              const now = Date.now();
              const elapsed = Math.min(
                (now - lastHeartbeatRef.current) / 1000,
                35,
              );
              lastHeartbeatRef.current = now;
              watchedTimeRef.current += elapsed;
              maxPositionRef.current = Math.max(
                maxPositionRef.current,
                videoRef.current.currentTime,
              );
              const videoDur = videoRef.current.duration || 0;
              onWatchProgress({
                watchedSeconds: watchedTimeRef.current,
                videoDuration: videoDur,
                maxPosition: maxPositionRef.current,
                completed: true,
              });
            }
            onEnded?.();
          }}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onError={handleVideoError}
          onLoadStart={() => {
            console.log("🎬 Video load started:", videoUrl);
            setIsLoading(true);
          }}
          onCanPlay={() => {
            console.log("✅ Video can play:", videoUrl);
            setVideoReady(true);

            // Enable seeking when seekable ranges are available
            if (videoRef.current?.seekable.length > 0) {
              setIsSeekable(true);
            }
          }}
          crossOrigin="anonymous"
          playsInline
          preload="auto"
        >
          {/* External WebVTT track if provided */}
          {subtitleUrl && (
            <track
              kind="subtitles"
              src={subtitleUrl}
              srcLang="en"
              label="English"
              onLoad={() => {
                console.log(
                  "âœ… Subtitle track loaded successfully:",
                  subtitleUrl,
                );
                setHasSubtitles(true);
              }}
              onError={(e) => {
                console.error(
                  "âŒ Subtitle track failed to load:",
                  subtitleUrl,
                  e,
                );
                console.error(
                  "This might be a CORS issue. Ensure the subtitle URL allows cross-origin access.",
                );
                setHasSubtitles(false);
              }}
            />
          )}
        </video>

        {/* Hidden video for preview thumbnails */}
        {videoUrl && (
          <video
            ref={previewVideoRef}
            src={videoUrl}
            crossOrigin="anonymous"
            preload="auto"
            muted
            playsInline
            style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
          />
        )}

        {/* Loading / buffering — a plain black cover, no glyph.
            This held three pulsing bars, itself a replacement for a spinner (spinning circles
            are out across this product). The bars are gone too: on a black frame they were the
            single most-looked-at thing in the player, and an indicator that says nothing more
            than "not yet" earns none of that attention. The cover still does the real job,
            isLoading ONLY, never mid-playback buffering. The cover used to come up on `waiting`
            too, which a browser fires on essentially every seek — so scrubbing blacked the
            whole frame out for a few hundred milliseconds and read as the video breaking. The
            job described above is a first-frame job: once playback has started the element is
            already showing the last decoded picture, and leaving that up while the next one
            arrives is both what every other player does and strictly better than black. */}
        <div
          className={`absolute inset-0 bg-black transition-opacity duration-200 ease-out ${
            isLoading ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          aria-hidden="true"
        />

        {/* THE VIDEO FAILED. SAY THAT, AND OFFER NOTHING ELSE.
            This used to be headed "Temporarily Unavailable" with a DOWNLOAD VIDEO button
            under it, which was wrong twice over. "Temporarily" is a promise — it tells
            someone whose generation died in the pipeline to come back later for a file that
            is never going to exist. And the download button points at the same object the
            player just failed to fetch: when there is no video, pressing it produces a second
            failure with a different error, and when there IS one, the person came here to
            watch rather than to collect a file. The player's own controls still carry the
            download for a video that plays, which is where that belongs.
            Reached only after the retry loop above has given up, so this is a settled state,
            not a blip. */}
        {error && (
          <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center">
            <div className="text-white text-center p-4 max-w-sm">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-400" />
              <h3 className="text-lg font-semibold mb-2">
                Video failed
              </h3>
              <p className="text-sm text-gray-300">{error}</p>
            </div>
          </div>
        )}

        {/* Play/Pause Overlay - clickable to toggle play/pause, hold for 2x speed, double-click for fullscreen */}
        {!isLoading && !error && (
          <div
            className="absolute inset-0 flex items-center justify-center cursor-pointer"
            onClick={handleVideoClick}
            onDoubleClick={handleVideoDoubleClick}
            onContextMenu={handleContextMenu}
            onMouseDown={handleSpeedHoldStart}
            onMouseUp={handleSpeedHoldEnd}
            onMouseLeave={handleSpeedHoldEnd}
            onTouchStart={handleSpeedHoldStart}
            onTouchEnd={handleSpeedHoldEnd}
          >
            {!isPlaying && !autoPlayPending && (
              <Play className="w-16 h-16 text-white opacity-80 hover:opacity-100 transition-opacity" />
            )}
            {/* Autoplay only got past the browser because we muted it — say so. */}
            {autoMuted && isPlaying && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleMuteUnmute();
                }}
                className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 hover:bg-black/85 transition-colors"
              >
                <VolumeX className="w-4 h-4" />
                Tap for sound
              </button>
            )}
            {/* 2x Speed Indicator when holding */}
            {isHoldingForSpeed && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
                2x Speed
              </div>
            )}
          </div>
        )}

        {/* Right-click Context Menu */}
        {showContextMenu && (
          <div
            ref={contextMenuRef}
            className="absolute bg-black/90 rounded-lg py-1 min-w-[160px] shadow-lg z-50"
            style={{ left: contextMenuPosition.x, top: contextMenuPosition.y }}
            onClick={(e) => e.stopPropagation()}
          >
            {onDownload && (
              <button
                onClick={() => {
                  setShowContextMenu(false);
                  onDownload();
                }}
                className="w-full text-left px-4 py-2 text-sm text-white/90 hover:bg-white/10 hover:text-white flex items-center gap-2 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download Video
              </button>
            )}
            <button
              onClick={() => {
                setShowContextMenu(false);
                handleFullscreen();
              }}
              className="w-full text-left px-4 py-2 text-sm text-white/90 hover:bg-white/10 hover:text-white flex items-center gap-2 transition-colors"
            >
              {isFullscreen ? (
                <Minimize className="w-4 h-4" />
              ) : (
                <Maximize className="w-4 h-4" />
              )}
              {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            </button>
          </div>
        )}
      </div>

      {/* Custom Subtitle Overlay - moves up when controls visible */}
      {subtitlesEnabled && currentCue && (
        <div
          className={`absolute left-0 right-0 text-center transition-all duration-200 pointer-events-none z-10 px-4 ${
            showControls ? "bottom-24" : "bottom-8"
          }`}
        >
          <span
            className="bg-black/80 text-white px-4 py-2 rounded-lg inline-block max-w-[90%] whitespace-pre-wrap"
            style={{
              fontSize: `${captionSize}px`,
              textShadow: "0 1px 3px rgba(0,0,0,0.8)",
            }}
          >
            {currentCue}
          </span>
        </div>
      )}

      {/* Title across the top of the frame.
          It rides `showControls` — the SAME state as the control bar below — so it obeys one
          visibility contract: shown on pointer enter/move, hidden after 3s of stillness even
          while the pointer is still over the video, hidden immediately on pointer leave, and
          re-shown by any movement. The page used to do this with `group-hover`, which cannot
          express the idle rule: CSS :hover has no notion of "the mouse stopped moving", so
          the title sat on the frame for as long as the pointer was anywhere over the player,
          long after the controls had faded out from under it. Same duration-150 as the bar,
          so the two fade together rather than in sequence. */}
      {showTitleOverlay && !error && !hideControls && title && (
        <div
          /* pr-[172px] reserves the top-right corner for the "Chat about this" pill. Both
             appear on `showControls`, so without it a long title runs straight under the
             pill — and the title is a 2-line clamp, so it does not wrap out of the way. */
          /* Sizes step down below sm. On a phone the player is ~359x224, and this chrome was
             built for a player five times that wide: at text-lg with a 172px reservation the
             title alone covered the top third of the frame. */
          className={`absolute top-0 left-0 right-0 z-30 p-2.5 pr-[108px] sm:p-4 sm:pr-[172px] pointer-events-none transition-opacity duration-150 ${
            showControls ? "opacity-100" : "opacity-0"
          }`}
          style={{
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.36) 55%, rgba(0,0,0,0) 100%)",
          }}
        >
          <p
            className="text-white text-[13px] sm:text-lg font-bold leading-snug drop-shadow [&_.katex]:text-[0.9em]"
            style={{ fontFamily: ROW_TITLE_FONT, ...CLAMP_2_LINES }}
            dangerouslySetInnerHTML={{ __html: safeRenderLatex(title) }}
          />
        </div>
      )}

      {/* Pause-to-ask, in two parts.
          The INVITATION appears on any stop after playback has begun — space bar, click, the
          control bar, or the video ending — because each of those is someone stopping to
          think. It is a labelled button rather than a bare icon: a lone speech bubble on a
          video reads as "comments", and the whole point is that a first-time viewer can tell
          what it does without clicking it.
          The PANEL is separate and only opens on that click. Not gated on `showControls`:
          the bar fades after 3s of stillness and neither of these should vanish with it. */}
      {chatVideoId && !error && !hideControls && (
        <>
          {!hideAskButton && (
          <button
            onClick={(e) => {
              // The play/pause hit area spans the frame; without this the click that opens
              // the panel also restarts the video underneath it.
              e.stopPropagation();
              if (onRequireAccount) { onRequireAccount(); return; }
              setAskOpen(true);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            /* top-3 right-3 — the panel's own anchor, so it reads as the thing the panel
               grows out of.
               Visibility rides `showControls`, the SAME state as the control bar and the
               title: appears on pointer enter/move, fades after 3s of stillness, gone on
               leave. Tying it to "paused" put it on screen at the one moment nobody is
               looking for a control, and left it absent while watching, which is exactly
               when a question occurs to someone. */
            className={`absolute right-2 top-2 sm:right-3 sm:top-3 z-30 flex items-center gap-1.5 sm:gap-2
              pl-2 pr-2.5 py-1 sm:pl-3 sm:pr-3.5 sm:py-2
              rounded-full bg-[#0f172a] border border-white/15 text-white text-[11px] sm:text-[13px] font-semibold
              shadow-[0_6px_24px_rgba(0,0,0,0.45)] hover:bg-[#1e293b] transition-all duration-150 ${
                showControls && !askOpen
                  ? "opacity-100"
                  : "opacity-0 pointer-events-none"
              }`}
          >
            <MessageCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
            {t("video_ask_cta")}
          </button>
          )}

          <VideoQuestionPanel
            videoId={chatVideoId}
            open={askOpen}
            onClose={() => setAskOpen(false)}
          />
        </>
      )}

      {/* Video Controls - Positioned relative to container (screen in fullscreen mode) */}
      {!error && !hideControls && (
        <div
          className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t ${isFullscreen ? "from-black/80 pt-12" : "from-black/50 pt-3 sm:pt-8"} to-transparent px-2 sm:px-4 transition-opacity duration-150 ${
            showControls ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          {/* Progress Bar - tall hit area wrapping thin visual bar */}
          <div
            ref={progressBarRef}
            className={`w-full py-2 sm:py-3 -my-1 mb-1 sm:mb-2 group relative ${
              isSeekable
                ? "cursor-pointer"
                : "cursor-not-allowed opacity-50"
            }`}
            onClick={isSeekable ? handleSeekTouch : undefined}
            onTouchEnd={isSeekable ? handleSeekTouch : undefined}
            onMouseMove={isSeekable ? handleProgressHover : undefined}
            onMouseLeave={handleProgressLeave}
          >
            {/* Preview thumbnail tooltip */}
            {previewTime !== null && effectiveDuration > 0 && (
              <div
                className="absolute bottom-full mb-1 pointer-events-none z-50 flex flex-col items-center"
                style={{
                  left: `clamp(80px, ${previewX}px, calc(100% - 80px))`,
                  transform: "translateX(-50%)",
                }}
              >
                <div className="rounded overflow-hidden border border-white/30 shadow-lg bg-black">
                  <canvas
                    key={previewFrame}
                    ref={(el) => {
                      if (!el || !previewCanvasRef.current) return;
                      const src = previewCanvasRef.current;
                      if (src.width && src.height) {
                        el.width = src.width;
                        el.height = src.height;
                        const ctx = el.getContext("2d");
                        ctx.drawImage(src, 0, 0);
                      }
                    }}
                    style={{ width: 160, height: 90, display: "block" }}
                  />
                </div>
                <span className="text-white text-xs mt-1 bg-black/80 px-1.5 py-0.5 rounded">
                  {formatTime(previewTime)}
                </span>
              </div>
            )}
            {/* Visible bar track */}
            {/* h-2 on a PHONE and h-1 on desktop was backwards. The rail is 8px on a 361px-wide
                player and 4px on a 1000px one, so the thing meant to be a hairline was twice
                as heavy exactly where the frame is smallest. It is a hairline at both sizes
                now; the touch target is the py-2 wrapper around it, not the rail itself. */}
            <div className={`w-full bg-white/20 h-[3px] md:h-1 transition-all relative group-hover:h-1.5`}>
              {/* Buffered range (light white bar — like YouTube's grey buffer bar) */}
              {effectiveDuration > 0 && (
                <div
                  className="absolute top-0 left-0 h-full bg-white/40 transition-all"
                  style={{ width: `${(bufferedEnd / effectiveDuration) * 100}%` }}
                />
              )}
              {/* Playback position (blue bar) */}
              <div
                className="bg-blue-500 h-full transition-all relative z-10"
                style={{
                  width: effectiveDuration
                    ? `${(currentTime / effectiveDuration) * 100}%`
                    : "0%",
                }}
              >
                {/* Seek handle — always visible on touch, hover on desktop */}
                {/* The handle scales with the rail — a 16px knob on a 3px rail reads as a
                    bead sitting on a wire rather than part of it. */}
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2.5 h-2.5 md:w-4 md:h-4 bg-blue-500 rounded-full shadow-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          </div>

          {/* Controls Row - below progress bar */}
          <div className="flex items-center justify-between pb-2 sm:pb-4">
            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* Play/Pause Button */}
              <button
                onClick={handlePlayPause}
                className="text-white hover:text-white/80 transition-colors p-1"
                disabled={isLoading}
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5 sm:w-7 sm:h-7" />
                ) : (
                  <Play className="w-5 h-5 sm:w-7 sm:h-7" />
                )}
              </button>

              {/* Time Display */}
              <div className="text-white text-[11px] sm:text-sm tabular-nums">
                {formatTime(currentTime)} / {formatTime(effectiveDuration)}
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Mute Button */}
              <button
                onClick={handleMuteUnmute}
                className="text-white/90 hover:text-white transition-colors p-1"
              >
                {isMuted ? (
                  <VolumeX className="w-4 h-4 sm:w-6 sm:h-6" />
                ) : (
                  <Volume2 className="w-4 h-4 sm:w-6 sm:h-6" />
                )}
              </button>

              {/* Ask about this video. Sits with CC because it belongs to the thing you are
                  WATCHING — parked in a sidebar tab it was a panel you had to remember was
                  there, and you ask a question at the moment the video raises it. */}
              {onChat && (
                <button
                  onClick={onChat}
                  className="text-white/70 hover:text-white transition-colors flex items-center"
                  title="Ask about this video"
                  aria-label="Ask about this video"
                >
                  <MessageCircle className="w-[19px] h-[19px]" />
                </button>
              )}

              {/* Subtitles (CC) Toggle */}
              <button
                onClick={toggleSubtitles}
                className={`transition-colors font-semibold text-xs sm:text-base px-1.5 sm:px-2 py-0.5 rounded ${hasSubtitles ? (subtitlesEnabled ? "bg-white/20 text-white" : "text-white/70") : "text-white/30 cursor-not-allowed"}`}
                title={
                  hasSubtitles
                    ? subtitlesEnabled
                      ? "Subtitles: On (C)"
                      : "Subtitles: Off (C)"
                    : "No subtitles available"
                }
                disabled={!hasSubtitles}
              >
                CC
              </button>

              {/* Playback Speed Control */}
              <div className="relative" ref={speedMenuRef}>
                <button
                  onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                  className="text-white/90 hover:text-white text-xs sm:text-base font-medium transition-colors"
                  title="Playback Speed"
                >
                  {playbackSpeed}x
                </button>
                {showSpeedMenu && (
                  <div className="absolute bottom-full right-0 mb-2 bg-black/90 rounded py-1 min-w-[70px] shadow-lg z-50">
                    {[0.5, 0.75, 1, 1.25, 1.5, 2].map((speed) => (
                      <button
                        key={speed}
                        onClick={() => handleSpeedChange(speed)}
                        className={`block w-full px-3 py-1.5 text-sm text-left hover:bg-white/10 transition-colors ${playbackSpeed === speed ? "text-white" : "text-white/70"}`}
                      >
                        {speed}x
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Follow Up Button */}
              {onFollowUp && (
                <div className="relative" ref={followUpRef}>
                  <button
                    onClick={() => setShowFollowUp(!showFollowUp)}
                    className={`transition-colors p-1 ${showFollowUp ? "text-white" : "text-white/70 hover:text-white"}`}
                    title="Ask a follow-up"
                  >
                    <MessageSquarePlus className="w-5 h-5" />
                  </button>
                  {showFollowUp && (
                    <div className="absolute bottom-full right-0 mb-2 bg-black/95 rounded-lg p-3 shadow-lg z-50 w-72">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={followUpText}
                          onChange={(e) => setFollowUpText(e.target.value)}
                          onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") handleFollowUpSend(); }}
                          placeholder={t('video_followup_placeholder')}
                          autoFocus
                          className="flex-1 px-2.5 py-1.5 text-sm bg-white/10 text-white border border-white/20 rounded-md focus:outline-none focus:ring-1 focus:ring-[#635BFF] placeholder-white/40"
                        />
                        <button
                          onClick={handleFollowUpSend}
                          disabled={!followUpText.trim()}
                          className="p-1.5 rounded-md bg-[#635BFF] text-white hover:bg-[#5851e0] active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {[
                          { key: 'video_chip_simpler', label: t('video_chip_simpler') },
                          { key: 'video_chip_harder', label: t('video_chip_harder') },
                          { key: 'video_chip_real_world', label: t('video_chip_real_world') },
                        ].map((chip) => (
                          <button
                            key={chip.key}
                            onClick={() => { onFollowUp(chip.label); setShowFollowUp(false); }}
                            className="px-2 py-0.5 text-xs text-white/50 bg-white/10 hover:bg-white/20 hover:text-white/80 rounded-full transition-colors"
                          >
                            {chip.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Share. Sits where Download used to: downloading is now one of the
                  options inside the share sheet, so a separate icon for it was two
                  controls doing one job. The error-state and right-click download
                  paths stay — those are fallbacks for when the player cannot play. */}
              {onShare && (
                <button
                  onClick={onShare}
                  className="text-white/90 hover:text-white transition-colors p-1"
                  title="Share"
                  aria-label="Share"
                >
                  <Share className="w-4 h-4 sm:w-6 sm:h-6" />
                </button>
              )}

              {/* Fullscreen Button */}
              <button
                onClick={handleFullscreen}
                className="text-white/90 hover:text-white transition-colors p-1"
                title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? (
                  <Minimize className="w-4 h-4 sm:w-6 sm:h-6" />
                ) : (
                  <Maximize className="w-4 h-4 sm:w-6 sm:h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(VideoPlayer);
